import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  createMasterUnit,
  getCityBranches,
  scanConsignment,
  CreateMasterUnitRequest,
  Branch,
  ScanConsignmentResponse,
  City
} from '../../services/masterUnitApi';
import { cityApi } from '../../services/cityApi';
import { clientApi, Client } from '../../services/clientApi';
import { profileApi } from '../../services/profileApi';
import { useTheme } from '../../contexts/ThemeContext';
import GenericButton from '../../components/GenericButton';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Stack,
  CircularProgress
} from '@mui/material';

const MasterUnitCreate: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);
  const isAdmin = user?.type === 'ADMIN';

  const [formData, setFormData] = useState<CreateMasterUnitRequest>({
    seal_number: '',
    mu_type: 'Sag Bag',
    type: 'Bulky',
    journey_type: 'Forward',
    origin: 0,
    destination: 0,
    inbound_branch_id: 0,
    client_id: 0,
    consignmentIds: []
  });

  const [cities, setCities] = useState<City[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [consignments, setConsignments] = useState<ScanConsignmentResponse[]>([]);
  const [scanInput, setScanInput] = useState('');
  const [scanFeedback, setScanFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [originCityName, setOriginCityName] = useState<string>('Loading...');

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setInitialLoading(true);

        // Fetch cities, clients, and user profile in parallel
        const [citiesResponse, clientsResponse, profileResponse] = await Promise.all([
          cityApi.getCities({ status: '1' }),
          clientApi.getClients({ status: '1' }),
          profileApi.getProfile()
        ]);

        setCities(citiesResponse.cities);
        setClients(clientsResponse.clients);

        // Get origin from user's branch city
        const user = profileResponse.data.user;
        if (user.branch) {
          // Find the city that matches the user's branch
          const branchCity = citiesResponse.cities.find(
            c => c.name.toLowerCase() === user.branch?.name?.toLowerCase()
          );
          if (branchCity) {
            setFormData(prev => ({ ...prev, origin: branchCity.id }));
            setOriginCityName(branchCity.name);
          } else {
            // Default to first city if branch city not found
            if (citiesResponse.cities.length > 0) {
              setFormData(prev => ({ ...prev, origin: citiesResponse.cities[0].id }));
              setOriginCityName(citiesResponse.cities[0].name);
            }
          }
        } else if (citiesResponse.cities.length > 0) {
          // Default to first city if no branch
          setFormData(prev => ({ ...prev, origin: citiesResponse.cities[0].id }));
          setOriginCityName(citiesResponse.cities[0].name);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load initial data');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'journey_type') {
      setFormData(prev => ({ ...prev, destination: 0, inbound_branch_id: 0, client_id: 0 }));
      setBranches([]);
    }
  };

  const handleOriginChange = (originId: number) => {
    const selectedCity = cities.find(c => c.id === originId);
    setFormData(prev => ({ ...prev, origin: originId }));
    setOriginCityName(selectedCity?.name || '');
  };

  const handleDestinationChange = async (destinationId: number) => {
    setFormData(prev => ({ ...prev, destination: destinationId, inbound_branch_id: 0 }));
    if (destinationId && formData.journey_type !== 'Failed Delivery') {
      try {
        const branchesData = await getCityBranches(destinationId);
        setBranches(branchesData);
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleScanConsignment = async () => {
    if (scanInput.length !== 17) {
      setScanFeedback('Consignment number must be 17 characters long');
      return;
    }
    try {
      const result = await scanConsignment({
        consignment_no: scanInput,
        destination_id: formData.destination || 0,
        journey_type: formData.journey_type,
        client_id: formData.client_id || undefined
      });
      if (formData.consignmentIds.includes(result.id)) {
        setScanFeedback('Consignment already added');
        return;
      }
      setFormData(prev => ({ ...prev, consignmentIds: [...prev.consignmentIds, result.id] }));
      setConsignments(prev => [...prev, result]);
      setScanFeedback('Consignment added successfully');
      setScanInput('');
    } catch (err: any) {
      setScanFeedback(err.message);
    }
  };

  const removeConsignment = (consignmentId: number) => {
    setFormData(prev => ({ ...prev, consignmentIds: prev.consignmentIds.filter(id => id !== consignmentId) }));
    setConsignments(prev => prev.filter(c => c.id !== consignmentId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.consignmentIds.length === 0) {
      setError('Please add at least one consignment');
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Build payload based on journey type
      const payload: CreateMasterUnitRequest = {
        seal_number: formData.seal_number,
        mu_type: formData.mu_type,
        type: formData.type,
        journey_type: formData.journey_type,
        origin: formData.origin,
        consignmentIds: formData.consignmentIds,
        destination: formData.journey_type === 'Failed Delivery' ? 0 : formData.destination,
        inbound_branch_id: formData.journey_type === 'Failed Delivery' ? 0 : formData.inbound_branch_id,
        client_id: formData.journey_type === 'Failed Delivery' ? formData.client_id : undefined,
      };

      await createMasterUnit(payload);
      navigate('/master-units');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    const basicFields = formData.seal_number && formData.mu_type && formData.type && formData.journey_type && formData.origin;
    if (formData.journey_type === 'Failed Delivery') {
      return basicFields && formData.client_id && formData.consignmentIds.length > 0;
    } else {
      return basicFields && formData.destination && formData.inbound_branch_id && formData.consignmentIds.length > 0;
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Create Master Unit</Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mt: 0.5 }}>Create a new master unit with consignments</Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                {isAdmin ? (
                  <TextField
                    label="Origin"
                    name="origin"
                    select
                    SelectProps={{ native: true }}
                    value={formData.origin}
                    onChange={(e) => handleOriginChange(Number(e.target.value))}
                    fullWidth
                  >
                    <option value={0}>Select Origin</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>{city.name}</option>
                    ))}
                  </TextField>
                ) : (
                  <TextField label="Origin" value={originCityName} disabled fullWidth />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Journey Type" name="journey_type" select SelectProps={{ native: true }} value={formData.journey_type} onChange={handleInputChange} fullWidth>
                  <option value="Forward">Forward</option>
                  <option value="Reverse">Reverse</option>
                  <option value="Failed Delivery">Failed Delivery</option>
                </TextField>
              </Grid>
              {formData.journey_type === 'Failed Delivery' ? (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField label="Customer" name="client_id" select SelectProps={{ native: true }} value={formData.client_id} onChange={handleInputChange} fullWidth>
                    <option value={0}>Select Customer</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </TextField>
                </Grid>
              ) : (
                <>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField label="Destination" name="destination" select SelectProps={{ native: true }} value={formData.destination} onChange={(e) => handleDestinationChange(Number(e.target.value))} fullWidth>
                      <option value={0}>Select Destination</option>
                      {cities.map(city => (
                        <option key={city.id} value={city.id}>{city.name}</option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField label="Branch" name="inbound_branch_id" select SelectProps={{ native: true }} value={formData.inbound_branch_id} onChange={handleInputChange} fullWidth>
                      <option value={0}>Select Branch</option>
                      {branches.map(branch => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Seal Number" name="seal_number" value={formData.seal_number} onChange={handleInputChange} fullWidth required />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="MU Type" name="mu_type" select SelectProps={{ native: true }} value={formData.mu_type} onChange={handleInputChange} fullWidth>
                  <option value="Sag Bag">Sag Bag</option>
                  <option value="Mother Bag">Mother Bag</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Type of Shipment" name="type" select SelectProps={{ native: true }} value={formData.type} onChange={handleInputChange} fullWidth>
                  <option value="Bulky">Bulky</option>
                  <option value="Non Bulky">Non Bulky</option>
                  <option value="COD">COD</option>
                  <option value="Express">Express</option>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {(formData.destination || formData.client_id) ? (
          <Card sx={{ bgcolor: theme.palette.background.paper }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Scan Consignments ({formData.consignmentIds.length})</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField placeholder="Enter 17-digit consignment number" value={scanInput} onChange={(e) => setScanInput(e.target.value)} fullWidth inputProps={{ maxLength: 17 }} />
                <Button variant="contained" onClick={handleScanConsignment}>Scan</Button>
              </Stack>
              {scanFeedback ? (
                <Alert severity={scanFeedback.includes('successfully') ? 'success' : 'error'} sx={{ mb: 2 }}>{scanFeedback}</Alert>
              ) : null}
              {consignments.length ? (
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Consignment #</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Booking Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Service Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Booking Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Weight (Grams)</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {consignments.map((consignment) => (
                        <TableRow key={consignment.id}>
                          <TableCell>{consignment.consignment_no}</TableCell>
                          <TableCell>{consignment.booking_type}</TableCell>
                          <TableCell>{consignment.service_type}</TableCell>
                          <TableCell>{new Date(consignment.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>{consignment.chargeable_weight}</TableCell>
                          <TableCell>{consignment.status}</TableCell>
                          <TableCell>
                            <Button color="error" onClick={() => removeConsignment(consignment.id)}>Remove</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {error ? (<Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>) : null}

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <GenericButton variant="outline" type="button" onClick={() => navigate('/master-units')}>Cancel</GenericButton>
          <GenericButton type="submit" disabled={!isFormValid() || loading}>{loading ? 'Creating...' : 'Create Master Unit'}</GenericButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default MasterUnitCreate;
